import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Combine,
  Copy,
  Edit,
  FileDown,
  FileText,
  Loader2,
  Plus,
  Search,
  Shuffle,
  Trash2,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  TableColumnVisibilityMenu,
  initialColumnVisibility,
  type TableColumnMenuItem,
} from '@/components/shared/TableColumnVisibilityMenu'
import { useAuthStore } from '@/store/auth.store'
import { showDeleteConfirm, showSuccess } from '@/lib/swal'
import { salesQuotationService, type SalesQuotationListRow } from '@/services/salesQuotationService'

const PAGE_SIZE = 10

/** Single / combined convert only when API `orderStatus` is 1 (confirmed). */
function isQuotationEligibleForConvert(r: SalesQuotationListRow): boolean {
  return r.orderStatus === 1 && Number(r.id) > 0 && r.status !== 'cancelled'
}

function isQuotationEditDisabled(r: SalesQuotationListRow): boolean {
  return r.status === 'cancelled' || r.orderStatus === 2
}

const QUOTATION_LIST_COLUMNS: TableColumnMenuItem[] = [
  { id: 'code', label: 'Code' },
  { id: 'reference', label: 'Reference' },
  { id: 'warehouse', label: 'Warehouse' },
  { id: 'orderDate', label: 'Order date' },
  { id: 'estDeliveryDate', label: 'Expected delivery date' },
  { id: 'total', label: 'Total' },
  { id: 'convert', label: 'Convert' },
  { id: 'actions', label: 'Actions' },
]

export const InventoryQuotationsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)

  const [rows, setRows] = useState<SalesQuotationListRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [convertingId, setConvertingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [combinedConvertSubmitting, setCombinedConvertSubmitting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [columnVisibility, setColumnVisibility] = useState(() =>
    initialColumnVisibility(QUOTATION_LIST_COLUMNS.map((c) => c.id)),
  )

  const visibleColumnCount = useMemo(
    () => QUOTATION_LIST_COLUMNS.filter((c) => columnVisibility[c.id]).length,
    [columnVisibility],
  )
  const tableColSpan = visibleColumnCount + 1

  const toggleColumn = (columnId: string) => {
    setColumnVisibility((prev) => ({ ...prev, [columnId]: !prev[columnId] }))
  }

  const loadPage = useCallback(async () => {
    if (!branchId) {
      setRows([])
      setTotalCount(0)
      return
    }
    try {
      setLoading(true)
      const { rows: data, totalCount: tc } = await salesQuotationService.getAllSalesQuotations(
        branchId,
        PAGE_SIZE,
        pageNumber,
      )
      setRows(data)
      setTotalCount(tc)
    } catch (error: unknown) {
      setRows([])
      setTotalCount(0)
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? String((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
          : ''
      toast.error(msg || 'Failed to load quotations.')
    } finally {
      setLoading(false)
    }
  }, [branchId, pageNumber])

  useEffect(() => {
    void loadPage()
  }, [loadPage])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.code, r.reference, r.warehouseName, r.status].some((x) => String(x).toLowerCase().includes(q)),
    )
  }, [rows, searchQuery])

  const eligibleRowsForCombined = useMemo(
    () => filtered.filter(isQuotationEligibleForConvert),
    [filtered],
  )

  const allEligibleSelected =
    eligibleRowsForCombined.length > 0 &&
    eligibleRowsForCombined.every((r) => selectedIds.includes(r.id))

  const eligibleIdSet = useMemo(
    () => new Set(eligibleRowsForCombined.map((r) => r.id)),
    [eligibleRowsForCombined],
  )

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => eligibleIdSet.has(id)))
  }, [eligibleIdSet])

  const toggleSelectRow = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const toggleSelectAllEligible = () => {
    if (allEligibleSelected) {
      const eligibleSet = new Set(eligibleRowsForCombined.map((r) => r.id))
      setSelectedIds((prev) => prev.filter((id) => !eligibleSet.has(id)))
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const r of eligibleRowsForCombined) next.add(r.id)
        return [...next]
      })
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const page = Math.min(pageNumber, totalPages)

  const fmtDate = (value?: string) => {
    if (!value) return '-'
    const d = new Date(value.includes('T') ? value : `${value}T12:00:00`)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  const handleCopy = () => {
    if (filtered.length === 0) return toast.error('Nothing to copy.')
    const header = ['Code', 'Reference', 'Warehouse', 'Order date', 'Expected delivery date', 'Total', 'Status']
    const lines = filtered.map((r) =>
      [r.code, r.reference, r.warehouseName, r.orderDate, r.estDeliveryDate, r.total.toFixed(2), r.status].join('\t'),
    )
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success('Copied.')
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) return toast.error('Nothing to export.')
    const header = 'Code,Reference,Warehouse,OrderDate,ExpectedDeliveryDate,Total,Status'
    const body = filtered
      .map((r) =>
        [r.code, r.reference, r.warehouseName, r.orderDate, r.estDeliveryDate, r.total.toFixed(2), r.status]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `inventory_quotations_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('CSV downloaded.')
  }

  const extractErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object' && 'message' in error) {
      const m = String((error as Error).message).trim()
      if (m) return m
    }
    if (error && typeof error === 'object' && 'response' in error) {
      const d = (error as { response?: { data?: unknown } }).response?.data
      if (d && typeof d === 'object' && !Array.isArray(d)) {
        const o = d as Record<string, unknown>
        const r = o.result ?? o.Result
        if (r && typeof r === 'object' && !Array.isArray(r)) {
          const m = String((r as { msg?: unknown }).msg ?? '').trim()
          if (m) return m
        }
        const msg = String(o.message ?? o.Message ?? '').trim()
        if (msg) return msg
      }
    }
    return ''
  }

  const handleCombinedConvertToOrder = async () => {
    const ids = selectedIds
      .filter((id) => eligibleIdSet.has(id))
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0)
    if (ids.length === 0) {
      toast.error('Select at least one confirmed quotation (order status 1) to convert.')
      return
    }
    try {
      setCombinedConvertSubmitting(true)
      await salesQuotationService.combinedConvertToOrder(ids)
      setSelectedIds([])
      await loadPage()
      await showSuccess(
        'Combined convert',
        'Selected quotations were converted to sales orders successfully.',
      )
    } catch (error: unknown) {
      toast.error(extractErrorMessage(error) || 'Failed to convert selected quotations.')
    } finally {
      setCombinedConvertSubmitting(false)
    }
  }

  const handleConvertToOrder = async (r: SalesQuotationListRow) => {
    if (!isQuotationEligibleForConvert(r)) {
      toast.error('Only confirmed quotations (order status 1) can be converted.')
      return
    }
    const id = Number(r.id)
    if (!Number.isFinite(id) || id <= 0) {
      toast.error('Invalid quotation.')
      return
    }
    try {
      setConvertingId(r.id)
      await salesQuotationService.convertToOrder(id)
      await loadPage()
      await showSuccess(
        'Converted to order',
        'The quotation was converted to a sales order successfully.',
      )
    } catch (error: unknown) {
      toast.error(extractErrorMessage(error) || 'Failed to convert to order.')
    } finally {
      setConvertingId(null)
    }
  }

  const onDeleteRow = async (r: SalesQuotationListRow) => {
    const id = Number(r.id)
    if (!Number.isFinite(id) || id <= 0) {
      toast.error('Invalid quotation.')
      return
    }
    const label = r.code || r.reference || 'this quotation'
    const ok = await showDeleteConfirm(label)
    if (!ok) return
    try {
      setDeletingId(r.id)
      await salesQuotationService.deleteQuotation(id)
      await loadPage()
      await showSuccess('Quotation deleted', 'The quotation was removed successfully.')
    } catch (error: unknown) {
      toast.error(extractErrorMessage(error) || 'Failed to delete quotation.')
    } finally {
      setDeletingId(null)
    }
  }

  const startIdx = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(page * PAGE_SIZE, totalCount)

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('navigation.inventorySalesQuotations')}</h1>
            <p className="text-muted-foreground text-sm">Quotation Details</p>
          </div>
        </div>
        <Button onClick={() => navigate('/inventory/quotations/new')} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add Quotation
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="w-full max-w-md">
              <label className="mb-2 block text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search (this page)"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => void handleCombinedConvertToOrder()}
                disabled={
                  combinedConvertSubmitting ||
                  loading ||
                  !branchId ||
                  convertingId !== null ||
                  deletingId !== null ||
                  selectedIds.length === 0
                }
              >
                {combinedConvertSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Combine className="mr-2 h-4 w-4" />
                )}
                Combined convert{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
              </Button>
              <TableColumnVisibilityMenu
                columns={QUOTATION_LIST_COLUMNS}
                visibility={columnVisibility}
                onToggle={toggleColumn}
                align="end"
                minVisible={1}
              />
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={loading}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={loading}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  <TableHead className="h-12 w-[52px] bg-primary/5 px-2 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={allEligibleSelected}
                        disabled={
                          eligibleRowsForCombined.length === 0 ||
                          combinedConvertSubmitting ||
                          loading ||
                          convertingId !== null ||
                          deletingId !== null
                        }
                        onCheckedChange={() => toggleSelectAllEligible()}
                        aria-label="Select all eligible quotations on this page"
                      />
                    </div>
                  </TableHead>
                  {columnVisibility.code ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Code
                    </TableHead>
                  ) : null}
                  {columnVisibility.reference ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Reference
                    </TableHead>
                  ) : null}
                  {columnVisibility.warehouse ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Warehouse
                    </TableHead>
                  ) : null}
                  {columnVisibility.orderDate ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Order date
                    </TableHead>
                  ) : null}
                  {columnVisibility.estDeliveryDate ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Expected delivery date
                    </TableHead>
                  ) : null}
                  {columnVisibility.total ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Total
                    </TableHead>
                  ) : null}
                  {columnVisibility.convert ? (
                    <TableHead className="h-12 w-[88px] bg-primary/5 px-2 text-center text-xs font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Convert
                    </TableHead>
                  ) : null}
                  {columnVisibility.actions ? (
                    <TableHead className="h-12 min-w-[88px] bg-primary/5 px-2 text-center text-xs font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Actions
                    </TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {!branchId ? (
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(tableColSpan, 1)}
                      className="py-12 text-center text-muted-foreground"
                    >
                      Select a branch to load quotations.
                    </TableCell>
                  </TableRow>
                ) : loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(tableColSpan, 1)}
                      className="py-12 text-center text-muted-foreground"
                    >
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="mt-4 text-muted-foreground">Loading…</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(tableColSpan, 1)}
                      className="py-12 text-center text-muted-foreground"
                    >
                      No data available in table
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r) => {
                    const cancelled = r.status === 'cancelled'
                    const editDisabled = isQuotationEditDisabled(r)
                    const canSelect = isQuotationEligibleForConvert(r)
                    return (
                      <TableRow
                        key={r.id}
                        className={
                          cancelled
                            ? 'border-b opacity-50'
                            : 'border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10'
                        }
                      >
                        <TableCell className="w-[52px] px-2 py-2 text-center">
                          <div className="flex justify-center">
                            <Checkbox
                              checked={selectedIds.includes(r.id)}
                              disabled={
                                !canSelect ||
                                combinedConvertSubmitting ||
                                loading ||
                                convertingId !== null ||
                                deletingId !== null
                              }
                              onCheckedChange={() => toggleSelectRow(r.id)}
                              aria-label={`Select quotation ${r.code || r.id}`}
                            />
                          </div>
                        </TableCell>
                        {columnVisibility.code ? (
                          <TableCell className="px-4 py-2 text-center text-sm">{r.code || '-'}</TableCell>
                        ) : null}
                        {columnVisibility.reference ? (
                          <TableCell className="px-4 py-2 text-center text-sm">{r.reference}</TableCell>
                        ) : null}
                        {columnVisibility.warehouse ? (
                          <TableCell className="px-4 py-2 text-center text-sm">{r.warehouseName || '-'}</TableCell>
                        ) : null}
                        {columnVisibility.orderDate ? (
                          <TableCell className="px-4 py-2 text-center text-sm">{fmtDate(r.orderDate)}</TableCell>
                        ) : null}
                        {columnVisibility.estDeliveryDate ? (
                          <TableCell className="px-4 py-2 text-center text-sm">{fmtDate(r.estDeliveryDate)}</TableCell>
                        ) : null}
                        {columnVisibility.total ? (
                          <TableCell className="px-4 py-2 text-center text-sm">{r.total.toFixed(2)}</TableCell>
                        ) : null}
                        {columnVisibility.convert ? (
                          <TableCell className="px-2 py-2 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              disabled={
                                !canSelect ||
                                convertingId !== null ||
                                deletingId !== null ||
                                combinedConvertSubmitting ||
                                loading
                              }
                              title="Convert to sales order"
                              aria-label="Convert to order"
                              onClick={() => void handleConvertToOrder(r)}
                            >
                              {convertingId === r.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                              ) : (
                                <Shuffle className="h-4 w-4" />
                              )}
                            </Button>
                          </TableCell>
                        ) : null}
                        {columnVisibility.actions ? (
                          <TableCell className="px-2 py-2 text-center">
                            <div className="flex items-center justify-center gap-0.5">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                                title="Edit"
                                aria-label="Edit"
                                disabled={
                                  editDisabled ||
                                  convertingId !== null ||
                                  deletingId !== null ||
                                  combinedConvertSubmitting
                                }
                                onClick={() => navigate(`/inventory/quotations/${r.id}/edit`)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                                title="Delete"
                                aria-label="Delete"
                                disabled={
                                  cancelled ||
                                  convertingId !== null ||
                                  deletingId !== null ||
                                  combinedConvertSubmitting
                                }
                                onClick={() => void onDeleteRow(r)}
                              >
                                {deletingId === r.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              Showing {startIdx} to {endIdx} of {totalCount} entries
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading || !branchId}
              >
                Prev
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium">{page}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading || !branchId}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
